import { API_URL, API_VERSION, SITE_URLS } from '@constants';
import { useData } from '@hooks/useData';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useCustomNavigate } from '@hooks/useCustomNavigate';

const ENDPOINT_URL = '/prd-reservations/my-stores-reservations/{uuid}';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type GetMyStoresProductReservationDetailInput = paths[url]['get']['parameters']['path'];
export type GetMyStoresProductReservationDetailSuccessResponse = paths[url]['get']['responses']['200']['content']['application/json'];

/**
 * Requests endpoint to get a user's stores' reservation.
 */
export const useGetMyStoresProductReservationDetail = () => {
    const { navigate } = useCustomNavigate();
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { setMyStoresProductReservationDetail, setIsLoadingMyStoresProductReservationDetail } = useData();

    const request = async (props: GetMyStoresProductReservationDetailInput) => {
        setIsLoadingMyStoresProductReservationDetail(true);

        try {
            const inputParameters = new URLSearchParams(getCommonParameters()).toString();

            const response = await fetch(`${API_URL}${ENDPOINT_URL.replace('{uuid}', props.uuid || '')}?${inputParameters}`, {
                method: 'GET',
                headers: getHeaders(),
            });

            if (!response.ok) {
                const error = new Error() as ApiError;

                try {
                    const json = await response.json();
                    error.message = json.message;
                    error.errors = json.errors;
                } catch (error: any) {
                    error.message = '';
                }
                
                error.statusCode = response.status;
                
                throw error;
            }

            const data: GetMyStoresProductReservationDetailSuccessResponse = await response.json();

            setMyStoresProductReservationDetail(data);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);

            if (error.statusCode === 404 || error.statusCode === 403) {
                // redirect to 404 page
                navigate(`/${SITE_URLS.pageNotFound}`);
            }
        } finally {
            setIsLoadingMyStoresProductReservationDetail(false);
        }
    };

    return { request };
};
